import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative rounded-lg border border-[var(--color-border)] p-4 [&>svg~*]:pl-7 [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4.5 [&>svg:has(~_.peer)]:top-4 [&>svg]:text-neutral-950 dark:border-[var(--color-border)] dark:[&>svg]:text-neutral-50",
  {
    variants: {
      variant: {
        default:
          "bg-white w-full text-neutral-800 dark:bg-neutral-950/30 dark:text-neutral-50",
        destructive:
          "border-red-500/50 w-full text-red-500 bg-red-50 dark:bg-red-800/10 dark:border-red-500 [&>svg]:text-red-500 dark:border-red-900/50 dark:text-red-800 dark:dark:border-red-900 dark:[&>svg]:text-red-800",
        jobMetrics:
          "rounded-md p-1.5 [&>svg~*]:pl-5 [&>svg]:left-1.5 [&>svg:has(~_.peer)]:top-1 text-xs border-error dark:border-error text-neutral-900 bg-red-200 dark:bg-neutral-900 [&>svg]:text-new-chart-7 dark:text-primary-400 dark:[&>svg]:text-new-chart-8 uppercase",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className, 'flex flex-col gap-1 type-body-s-regular')}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("peer type-body-s-medium leading-none tracking-normal", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("type-body-s-regular [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
